import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import {
  faCheckCircle,
  faHourglass,
  faSearch,
  faTachometer,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Columns, DashboardModule,
  LoadingModule,
} from '@domain-group/suit-theme'

import { capitalizeString, getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import useAgencySelector from '@hooks/useAgencySelector'
import useCampaignQuotes from '@hooks/useCampaignQuotes'
import useCurrentUser from '@hooks/useCurrentUser'
import useFlipperKey from '@hooks/useFlipperKey'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import PageContext from '@contexts/pageContext'

import defaultRequestOptions from '@sections/Client/packs/Overviews/defaultRequestOptions'

import type { UseFiltersPayload } from '@domain-group/react-hooks'
import type { CampaignQuoteStatusKeys } from '@models/campaignQuote'

import QuotesBlankState from '../QuotesBlankState'
import QuotesTable from '../QuotesTable'

const buildPageFilterFields = (validateQuote: boolean) => {
  const boxProps = { marginBottom: 'large' }
  const style = { marginBottom: 16 }
  const defaultFilters = [{
    component: 'DynamicInput',
    componentProps: {
      inputComponent: 'input',
      style,
    },
    key: 'string',
    label: 'Filter by Title',
  },
  {
    component: 'DynamicInput',
    componentProps: {
      inputComponent: 'input',
      inputType: 'date',
      style,
    },
    key: 'created_date_start',
    label: 'Start Date (Created)',
  },
  {
    component: 'DynamicInput',
    componentProps: {
      inputComponent: 'input',
      inputType: 'date',
      style,
    },
    key: 'created_date_end',
    label: 'End Date (Created)',
  },
  {
    callbacks: {
      buildSearchRequestOptions: () => ({ actable_types: ['OfficeAdministrator'] }),
    },
    component: 'EntitySelector',
    componentProps: {
      boxProps,
      label: 'Priority For',
      entityKey: 'users',
      entityTitleKey: 'full_name',
    },
    key: 'priority_quotes_by_user_id',
  },
  ]

  const allFilters = [
    ...defaultFilters,
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'campaignQuoteTemplates',
      },
      key: 'campaign_quote_template_id',
    },
  ]

  if (validateQuote){
    return allFilters
  }

  return defaultFilters
}

interface PageContentProps {
  filterPayload: UseFiltersPayload,
}

const PageContent = (props: PageContentProps) => {
  const { filterPayload } = props
  const { pageFilters, persistedFilters } = filterPayload

  // @ts-expect-error TS2339
  const { priority_quotes_by_user_id: priorityQuotesUserId } = pageFilters || {}

  const { isEnabled: quoteValidationIsEnabled } = useFlipperKey('quote_validation')

  const { currentUser, isAgent, isEbuAdmin } = useCurrentUser()
  const selectedPriorityUserId = priorityQuotesUserId || currentUser.id

  const { selectedAgencyId } = useAgencySelector()

  useLocation()

  const { status }: { status?: CampaignQuoteStatusKeys | 'all' } = parsePermittedQueryParams(
    getQueryParamsFromHash(),
    ['status'],
  )
  const currentStatus = status || 'all'

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters, updateFilters,
    },
    defaultFilters,
  } = pageContext

  const quoteOverviewsPayload = useCampaignQuotes({
    filters: {
      agency_id: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
      order_by: 'updated_at DESC',
      status_key: currentStatus,
      sort_by_priority_campaign_for_user_id: !priorityQuotesUserId ? currentUser.id : undefined,
      // eslint-disable-next-line max-len
      // @ts-expect-error TS2339
      campaign_quote_template_id: pageFilters.campaign_quote_template_id ? pageFilters.campaign_quote_template_id : undefined,
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: {
      ...defaultRequestOptions.campaignQuote,
    },
    sortBy: 'cache_key desc',
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuotes,
    hasFilteredCampaignQuotes,
    filteredCampaignQuotesCount,
    loading,
  } = quoteOverviewsPayload

  return (
    <>
      <Helmet>
        <title>Quotes | Realhub</title>
      </Helmet>

      <PageHeader
        nestedNavigation
        activeTabBarItemKey={currentStatus}
        tabBarItems={[
          {
            icon: faTachometer,
            key: 'all',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: '' }),
            title: 'All Quotes',
          },
          {
            icon: faHourglass,
            key: 'pending',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'pending' }),
            title: 'Pending',
          },
          {
            icon: faSearch,
            key: 'awaiting_review',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'awaiting_review' }),
            title: 'Awaiting Review',
          },
          {
            icon: faCheckCircle,
            key: 'approved',
            // @ts-expect-error TS2353
            onClick: () => updateFilters({ status: 'approved' }),
            title: 'Approved',
          },
        ]}
        title="Quotes"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation
              locationKey="agency.agencyDashboard.quotes.banner"
            />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCampaignQuotes && <QuotesBlankState />}

            {!loading && hasFilteredCampaignQuotes && (
              <DashboardModule
                contentBoxProps={{ flexDirection: 'column' }}
                key={currentStatus}
                title={currentStatus === 'awaiting_review'
                  ? 'Quotes Awaiting Review'
                  : `${capitalizeString(currentStatus)} Quotes`}
              >
                <QuotesTable
                  currentStatus={currentStatus}
                  // @ts-expect-error TS2322
                  filteredCampaignQuotes={filteredCampaignQuotes}
                  selectedPriorityUserId={selectedPriorityUserId}
                />

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredCampaignQuotesCount}
                  loading={loading}
                  showBorderTop={false}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation
              locationKey="agency.agencyDashboard.quotes.sidebar"
            />

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              externalFilters={persistedFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields(quoteValidationIsEnabled)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
